/**
 * The browser's single source of truth for the signed-in user's active school
 * memberships. Authorization remains enforced by the database and server API;
 * this state only drives navigation and role selection in the UI.
 */
export const useSchoolMemberships = () => {
  const isSystemAdmin = useState<boolean>('school-system-admin', () => false)
  const memberships = useState<SchoolRole[]>('school-memberships', () => [])
  const loadedForUserId = useState<string | null>(
    'school-memberships-user-id',
    () => null,
  )
  const pending = useState<boolean>('school-memberships-pending', () => false)
  const user = useSupabaseUser()

  const clear = () => {
    isSystemAdmin.value = false
    memberships.value = []
    loadedForUserId.value = null
  }

  const load = async (force = false) => {
    const userId = user.value?.id
    if (!userId) {
      clear()
      return memberships.value
    }

    if (!force && loadedForUserId.value === userId) return memberships.value

    pending.value = true
    try {
      const result = await $fetch<{
        data?: SchoolRole[]
        isSystemAdmin?: boolean
        error?: unknown
      }>('/api/userSchoolRoles', {
        headers: import.meta.server ? useRequestHeaders(['cookie']) : undefined,
      })
      if (user.value?.id !== userId) return memberships.value
      if (result.error) throw new Error('Unable to load school access')
      isSystemAdmin.value = result.isSystemAdmin === true
      memberships.value = result.data ?? []
      loadedForUserId.value = userId
      return memberships.value
    } finally {
      pending.value = false
    }
  }

  return { memberships, isSystemAdmin, pending, load, clear }
}
